/** Campos e entradas reutilizados nas telas. */
import {
  AutocompleteInput,
  CreateButton,
  ReferenceInput,
  useCreate,
  useNotify,
  DeleteButton,
  NumberInput,
  SaveButton,
  TextInput,
  type TextInputProps,
  Toolbar,
  TopToolbar,
  usePermissions,
  useRecordContext,
  type NumberInputProps,
} from "react-admin";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import type { Permissoes } from "./lib/provedores";
import { formatarData, formatarPercentual, formatarTelefone, fracaoParaPercentual, linkWhatsapp, percentualParaFracao } from "./lib/formato";
import Link from "@mui/material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

interface CampoProps {
  source: string;
  label?: string;
  sortable?: boolean;
}

function valorDoCampo(registro: Record<string, unknown> | undefined, source: string): unknown {
  return registro ? registro[source] : undefined;
}

/** Mostra uma fração gravada no banco como percentual ("0.15" → "15%"). */
export function CampoPercentual({ source }: CampoProps) {
  const registro = useRecordContext();
  return <Typography variant="body2">{formatarPercentual(valorDoCampo(registro, source) as number | null)}</Typography>;
}

/** Mostra uma data ISO (yyyy-mm-dd) no formato brasileiro, sem conversão de fuso. */
export function CampoData({ source }: CampoProps) {
  const registro = useRecordContext();
  return <Typography variant="body2">{formatarData(valorDoCampo(registro, source) as string | null)}</Typography>;
}

/** Entrada em percentual (digita 15, grava 0.15). */
export function EntradaPercentual(props: Omit<NumberInputProps, "format" | "parse">) {
  return (
    <NumberInput
      {...props}
      format={fracaoParaPercentual}
      parse={percentualParaFracao}
      min={0}
      max={99.99}
      step={0.01}
      InputProps={{ endAdornment: <span style={{ marginLeft: 4 }}>%</span> }}
    />
  );
}

/** Tela estreita (celular): listas trocam a tabela por lista simples. */
export function useCelular() {
  return useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
}

/** Nulo no lugar de texto vazio (o banco distingue "não informado" de texto em branco). */
export const vazioParaNulo = (valor: unknown) => (valor === "" ? null : valor);

/** Barra do formulário de cadastro: Salvar para todos; Excluir só para o admin (o RLS também garante). */
export function BarraFormulario() {
  const { permissions } = usePermissions<Permissoes | null>();
  return (
    <Toolbar sx={{ justifyContent: "space-between" }}>
      <SaveButton />
      {permissions?.papel === "admin" && <DeleteButton mutationMode="pessimistic" />}
    </Toolbar>
  );
}

/** Ações da lista: só o botão de cadastrar. */
export function AcoesLista() {
  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  );
}

const telefoneParaDigitos = (valor: unknown) => {
  const d = String(valor ?? "").replace(/\D/g, "").slice(0, 11);
  return d === "" ? null : d;
};
const validarTelefone = (valor: unknown) => {
  const d = String(valor ?? "").replace(/\D/g, "");
  return d === "" || d.length === 10 || d.length === 11 ? undefined : "Informe DDD + número (10 ou 11 dígitos).";
};

/** Telefone com DDD: máscara (99) 99999-9999 ao digitar; grava só os dígitos. */
export function EntradaTelefone(props: Omit<TextInputProps, "format" | "parse">) {
  return (
    <TextInput
      {...props}
      format={formatarTelefone}
      parse={telefoneParaDigitos}
      validate={validarTelefone}
      inputProps={{ inputMode: "tel", maxLength: 15 }}
      placeholder="(99) 99999-9999"
    />
  );
}

/**
 * Escolha em lista que cresce com o uso (D44): seleciona um item existente ou cadastra um novo
 * digitando o nome; o novo fica disponível para os próximos cadastros. Usado em setor, grupo e modalidade.
 */
export function EntradaListaCrescente({
  source,
  reference,
  rotuloNovo,
  label,
  helperText,
}: {
  source: string;
  reference: "setor_fornecedor" | "grupo_produto" | "modalidade";
  rotuloNovo: string;
  label?: string;
  helperText?: string;
}) {
  const [create] = useCreate();
  const notify = useNotify();
  async function criar(texto?: string) {
    const nome = (texto ?? "").trim();
    if (!nome) return undefined;
    try {
      const novo = await create(reference, { data: { nome } }, { returnPromise: true });
      notify(`${rotuloNovo} "${nome}" cadastrado.`, { type: "info" });
      return novo;
    } catch (e) {
      notify(`Não foi possível cadastrar: ${(e as Error).message}`, { type: "error" });
      return undefined;
    }
  }
  return (
    <ReferenceInput source={source} reference={reference} filter={{ ativo: true }} sort={{ field: "nome", order: "ASC" }} perPage={200}>
      <AutocompleteInput
        optionText="nome"
        fullWidth
        onCreate={criar}
        createItemLabel={`Cadastrar ${rotuloNovo.toLowerCase()} "%{item}"`}
        parse={vazioParaNulo}
        filterToQuery={(texto: string) => (texto ? { "nome@ilike": texto } : {})}
        {...(label ? { label } : {})}
        {...(helperText ? { helperText } : {})}
      />
    </ReferenceInput>
  );
}

/** Número de WhatsApp como link que abre a conversa (nova aba). Não dispara o clique da linha da lista. */
export function LinkWhatsapp({ numero }: { numero: string | null | undefined }) {
  const url = linkWhatsapp(numero);
  if (!url) return <Typography variant="body2">—</Typography>;
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      underline="hover"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}
    >
      <WhatsAppIcon fontSize="small" />
      {formatarTelefone(numero)}
    </Link>
  );
}
